import type { MapleEngine } from '../../../../../../packages/engine-core/src';

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as Cesium from 'cesium';

import { initCesium, destroyCesium, getViewer, setCameraToPlane, setControlsEnabled, setRenderOptimization } from '../world/cesiumWorld';
import { PlanePhysics } from '../plane/planePhysics';
import { PlaneController } from '../plane/planeController';
import { movePosition } from '../utils/math';
import { calculateDistance, reverseGeocode } from '../world/regions';
import { HUD } from '../ui/hud';
import { JetFlame } from '../plane/jetFlame';
import { WeaponSystem } from '../systems/weaponSystem';
import { soundManager } from '../utils/soundManager';
import { NPCSystem } from '../systems/npcSystem';
import { DialogueSystem } from '../systems/dialogueSystem';
import { particles } from '../utils/particles';

const ASSET_BASE = '/os/games/flightsim';

const States = {
  MENU: 'MENU',
  PICK_SPAWN: 'PICK_SPAWN',
  TRANSITIONING: 'TRANSITIONING',
  FLYING: 'FLYING',
  PAUSED: 'PAUSED',
  CRASHED: 'CRASHED',
} as const;

type FlightSimState = (typeof States)[keyof typeof States];

type FlightSimRefs = {
  root: HTMLDivElement;
  cesiumContainer: HTMLDivElement;
  threeContainer: HTMLDivElement;
  minimapContainer: HTMLDivElement;
  pauseMinimapContainer: HTMLDivElement;
};

type RuntimeState = {
  lon: number;
  lat: number;
  alt: number;
  heading: number;
  pitch: number;
  roll: number;
  speed: number;
  throttle: number;
  score: number;
  weaponSystem: WeaponSystem | null;
  yaw?: number;
  isBoosting?: boolean;
  npcs?: any[];
};

type LoadingStatus = {
  audio: boolean;
  model: boolean;
  cesium: boolean;
  globe: boolean;
  failed: boolean;
};

export function createFlightSimGame(engine: MapleEngine, refs: FlightSimRefs) {
  const { root, cesiumContainer, threeContainer, minimapContainer, pauseMinimapContainer } = refs;
  if (!root || !cesiumContainer || !threeContainer || !minimapContainer || !pauseMinimapContainer) {
    return;
  }

  const query = <T extends HTMLElement>(selector: string) => root.querySelector(selector) as T | null;
  const byId = <T extends HTMLElement>(id: string) => root.querySelector(`#${id}`) as T | null;
  const addCleanup: Array<() => void> = [];
  const listen = <K extends keyof WindowEventMap>(target: Window, type: K, handler: (event: WindowEventMap[K]) => void, options?: AddEventListenerOptions | boolean) => {
    target.addEventListener(type, handler as EventListener, options);
    addCleanup.push(() => target.removeEventListener(type, handler as EventListener, options));
  };
  const listenDocument = <K extends keyof DocumentEventMap>(target: Document, type: K, handler: (event: DocumentEventMap[K]) => void, options?: AddEventListenerOptions | boolean) => {
    target.addEventListener(type, handler as EventListener, options);
    addCleanup.push(() => target.removeEventListener(type, handler as EventListener, options));
  };
  const listenElement = <K extends keyof HTMLElementEventMap>(target: HTMLElement, type: K, handler: (event: HTMLElementEventMap[K]) => void, options?: AddEventListenerOptions | boolean) => {
    target.addEventListener(type, handler as EventListener, options);
    addCleanup.push(() => target.removeEventListener(type, handler as EventListener, options));
  };

  let destroyed = false;
  let currentState: FlightSimState = States.MENU;

  let gameSettings = {
    graphicsQuality: 'medium',
    antialiasing: true,
    fogEffects: true,
    mouseSensitivity: 0.2,
    showHud: true,
    showHorizonLines: false,
    soundEnabled: true,
    minimapRange: 10,
  };

  const state: RuntimeState = {
    lon: 106.8272,
    lat: -6.1754,
    alt: 1000,
    heading: 0,
    pitch: 0,
    roll: 0,
    speed: 0,
    throttle: 0,
    score: 0,
    weaponSystem: null,
  };

  let currentRegionName: string | null = null;
  let lastGeocodeTime = 0;
  let lastGeocodePos = { lon: 0, lat: 0 };
  const GEOCODE_INTERVAL = 10_000;
  const GEOCODE_MIN_DIST = 1_000;

  let lastGPWSWarningTime = 0;
  const GPWS_COOLDOWN = 1800;
  let gpwsActive = false;
  let pauseStartTime = 0;

  let scene: THREE.Scene;
  let camera: THREE.PerspectiveCamera;
  let renderer: THREE.WebGLRenderer;
  let planeModel: THREE.Group | undefined;
  let jetFlames: JetFlame[] = [];
  let mixer: THREE.AnimationMixer | undefined;
  let physics = new PlanePhysics();
  let controller = new PlaneController({ engine, element: root });
  let hud = new HUD();
  let npcSystem: NPCSystem | undefined;
  let weaponSystem: WeaponSystem | null = null;
  const dialogueSystem = new DialogueSystem();

  let fps = 0;
  let frameCount = 0;
  let lastFpsUpdate = performance.now();
  let elapsedForMenuClock = 0;

  const BASE_PLANE_POS = new THREE.Vector3(0, -0.8, -2.75);
  const visualOffset = new THREE.Vector3().copy(BASE_PLANE_POS);
  const visualRotation = new THREE.Euler(0, 0, 0);
  let boostRoll = 0;
  let currentBoostZOffset = 0;
  let boostRollDirection = 1;
  let lastIsBoosting = false;
  let initialCameraView: { destination: Cesium.Cartesian3; orientation: { heading: number; pitch: number; roll: number } } | null = null;
  let lastThrottleLevel = 0;
  const DEFAULT_FLIGHT_FOV = 75;
  const SECONDARY_ZOOM_FOV = 38;
  let zoomBlend = 0;

  let spawnMarker: Cesium.Entity | null = null;
  let flightStartTime = 0;
  let lastCrashCheck = 0;
  let globeTrackerCleanup: (() => void) | null = null;
  let spawnHandler: Cesium.ScreenSpaceEventHandler | null = null;
  let lastWidth = 0;
  let lastHeight = 0;

  const mainMenu = byId<HTMLDivElement>('mainMenu');
  const pauseMenu = byId<HTMLDivElement>('pauseMenu');
  const crashMenu = byId<HTMLDivElement>('crashMenu');
  const uiContainer = byId<HTMLDivElement>('uiContainer');
  const spawnInstruction = byId<HTMLDivElement>('spawnInstruction');
  const confirmSpawnBtn = byId<HTMLButtonElement>('confirmSpawnBtn');
  const startBtn = byId<HTMLButtonElement>('startBtn');
  const loadingIndicator = byId<HTMLDivElement>('loadingIndicator');
  const loadingText = byId<HTMLDivElement>('loadingText');

  const loadingStatus: LoadingStatus = {
    audio: false,
    model: false,
    cesium: false,
    globe: false,
    failed: false,
  };

  function syncPointerLockState() {
    const shouldCaptureMouse = currentState === States.FLYING;
    controller.setPointerLockEnabled(shouldCaptureMouse);
    root.style.cursor = shouldCaptureMouse ? 'crosshair' : 'auto';
  }

  function closeAllModals() {
    root.querySelectorAll('.modal').forEach((modal) => modal.classList.add('hidden'));
  }

  function getMenuTimeElement() {
    return byId<HTMLSpanElement>('menu-time');
  }

  function setMenuTimeNow() {
    const menuTimeElem = getMenuTimeElement();
    if (menuTimeElem) {
      menuTimeElem.textContent = new Date().toISOString().split('.')[0] + 'Z';
    }
  }

  function updateLoadingUI() {
    if (!loadingIndicator || !loadingText || !startBtn) return;

    if (currentState === States.FLYING || currentState === States.TRANSITIONING) {
      loadingIndicator.classList.add('hidden');
      return;
    }

    let message = '';
    const isCoreLoaded = loadingStatus.model && loadingStatus.cesium;
    const isAllLoaded = isCoreLoaded && loadingStatus.audio && loadingStatus.globe;

    if (loadingStatus.failed) {
      message = 'Loading Failed. Please Refresh.';
    } else if (!isCoreLoaded) {
      if (!loadingStatus.model) message = 'Loading Aircraft Model...';
      else if (!loadingStatus.cesium) message = 'Loading Satellite Imagery...';
    } else if (!isAllLoaded) {
      if (!loadingStatus.audio) message = 'Finalizing Audio...';
      else if (!loadingStatus.globe) message = 'Finalizing Globe Surface...';
    }

    if (message) {
      loadingText.textContent = message;
      startBtn.disabled = !isCoreLoaded;
      startBtn.style.pointerEvents = isCoreLoaded ? 'auto' : 'none';
      loadingIndicator.classList.toggle('hidden', isCoreLoaded && !loadingStatus.failed);

      if (loadingStatus.failed) {
        loadingText.style.color = '#f00';
        const spinner = loadingIndicator.querySelector<HTMLElement>('.spinner');
        if (spinner) {
          spinner.style.borderColor = 'rgba(255, 0, 0, 0.3)';
          spinner.style.borderTopColor = '#f00';
        }
      }
    } else {
      loadingIndicator.classList.add('hidden');
      startBtn.disabled = false;
      startBtn.style.pointerEvents = 'auto';
    }
  }

  function saveSettings() {
    localStorage.setItem('flightSimSettings', JSON.stringify(gameSettings));
  }

  function updateSettingsUI() {
    const graphicsQuality = byId<HTMLSelectElement>('graphicsQuality');
    const antialiasing = byId<HTMLInputElement>('antialiasing');
    const fogEffects = byId<HTMLInputElement>('fogEffects');
    const sensitivitySlider = byId<HTMLInputElement>('sensitivitySlider');
    const sensitivityValue = byId<HTMLSpanElement>('sensitivityValue');
    const showHud = byId<HTMLInputElement>('showHud');
    const showHorizonLines = byId<HTMLInputElement>('showHorizonLines');
    const soundEnabled = byId<HTMLInputElement>('soundEnabled');
    const minimapRange = byId<HTMLSelectElement>('minimapRange');

    if (graphicsQuality) graphicsQuality.value = gameSettings.graphicsQuality;
    if (antialiasing) antialiasing.checked = gameSettings.antialiasing;
    if (fogEffects) fogEffects.checked = gameSettings.fogEffects;
    if (sensitivitySlider) sensitivitySlider.value = `${gameSettings.mouseSensitivity}`;
    if (sensitivityValue) sensitivityValue.textContent = `${gameSettings.mouseSensitivity}`;
    if (showHud) showHud.checked = gameSettings.showHud;
    if (showHorizonLines) showHorizonLines.checked = gameSettings.showHorizonLines;
    if (soundEnabled) soundEnabled.checked = gameSettings.soundEnabled;
    if (minimapRange) minimapRange.value = gameSettings.minimapRange.toString();
  }

  function applySettings() {
    controller.setSensitivity(gameSettings.mouseSensitivity);
    hud.setMinimapRange(gameSettings.minimapRange);
    hud.setShowHorizonLines(gameSettings.showHorizonLines);

    if (soundManager?.listener) {
      soundManager.listener.setMasterVolume(gameSettings.soundEnabled ? 1.0 : 0.0);
    }

    const viewer = getViewer();
    if (viewer) {
      if (gameSettings.graphicsQuality === 'low') {
        viewer.resolutionScale = 0.5;
        viewer.scene.globe.maximumScreenSpaceError = 4;
      } else if (gameSettings.graphicsQuality === 'medium') {
        viewer.resolutionScale = 0.75;
        viewer.scene.globe.maximumScreenSpaceError = 2;
      } else {
        viewer.resolutionScale = 1.0;
        viewer.scene.globe.maximumScreenSpaceError = 1.3;
      }

      viewer.scene.postProcessStages.fxaa.enabled = gameSettings.antialiasing;
      viewer.scene.fog.enabled = gameSettings.fogEffects;
      (viewer.scene.atmosphere as any).show = gameSettings.fogEffects;
    }

    ['hud-top-left', 'hud-top-right', 'hud-speed-box', 'hud-alt-box', 'coords', 'minimap-container'].forEach((id) => {
      const element = byId<HTMLElement>(id);
      if (element) {
        element.style.display = gameSettings.showHud ? 'block' : 'none';
      }
    });
  }

  function loadSettings() {
    const saved = localStorage.getItem('flightSimSettings');
    if (saved) {
      try {
        gameSettings = { ...gameSettings, ...JSON.parse(saved) };
      } catch (error) {
        console.error('Failed to load flight sim settings', error);
      }
    }

    applySettings();
    updateSettingsUI();
  }

  async function initUserLocation() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        state.lat = data.latitude;
        state.lon = data.longitude;
      }
    } catch {
      return;
    }
  }

  function stopAllFlyingSounds(fadeOut = 0.5) {
    soundManager.stopAll(fadeOut);
  }

  function pauseGameplaySounds() {
    pauseStartTime = Date.now();
    soundManager.pauseAll();
  }

  function resumeGameplaySounds() {
    const pauseDuration = Date.now() - pauseStartTime;
    if (lastGPWSWarningTime > 0) {
      lastGPWSWarningTime += pauseDuration;
    }
    soundManager.resumeAll();
  }

  function setupButtonSounds() {
    const handleMouseOver = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest('button, .menu-btn, .clickable-ui');
      if (!target || !root.contains(target)) return;
      const typedTarget = target as HTMLElement & { _hovered?: boolean };
      if (!typedTarget._hovered) {
        soundManager.play('button-hover');
        typedTarget._hovered = true;
        const onMouseLeave = () => {
          typedTarget._hovered = false;
        };
        typedTarget.addEventListener('mouseleave', onMouseLeave, { once: true });
      }
    };

    const handleClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest('button, .menu-btn, .clickable-ui, #search-toggle-btn');
      if (target && root.contains(target)) {
        soundManager.play('button-click');
      }
    };

    listenDocument(document, 'mouseover', handleMouseOver, true);
    listenDocument(document, 'click', handleClick, true);
  }

  async function initSounds() {
    soundManager.init(camera);

    await Promise.all([
      soundManager.loadSound('boost', `${ASSET_BASE}/assets/sounds/boost.mp3`, false, 0.35),
      soundManager.loadSound('throttle', `${ASSET_BASE}/assets/sounds/throttle.mp3`, false, 0.4),
      soundManager.loadSound('explode', `${ASSET_BASE}/assets/sounds/explode.mp3`, false, 0.75),
      soundManager.loadSound('explosion-1', `${ASSET_BASE}/assets/sounds/explosion-1.mp3`, false, 0.8),
      soundManager.loadSound('explosion-2', `${ASSET_BASE}/assets/sounds/explosion-2.mp3`, false, 0.8),
      soundManager.loadSound('explosion-3', `${ASSET_BASE}/assets/sounds/explosion-3.mp3`, false, 0.8),
      soundManager.loadSound('ambient-crash', `${ASSET_BASE}/assets/sounds/ambient.mp3`, true, 0.5),
      soundManager.loadSound('weapon-warning', `${ASSET_BASE}/assets/sounds/weapon-warning-1.mp3`, false, 1.0),
      soundManager.loadSound('jet-engine', `${ASSET_BASE}/assets/sounds/jet-engine.mp3`, true, 0.5),
      soundManager.loadSound('spawn', `${ASSET_BASE}/assets/sounds/spawn.mp3`, false, 0.5),
      soundManager.loadSound('roll', `${ASSET_BASE}/assets/sounds/roll.mp3`, true, 0.75),
      soundManager.loadSound('pitch', `${ASSET_BASE}/assets/sounds/pitch.mp3`, true, 0.75),
      soundManager.loadSound('button-click', `${ASSET_BASE}/assets/sounds/button-click.mp3`, false, 1.0),
      soundManager.loadSound('weapon-switch', `${ASSET_BASE}/assets/sounds/weapon-switch.mp3`, false, 0.75),
      soundManager.loadSound('button-hover', `${ASSET_BASE}/assets/sounds/button-hover.mp3`, false, 0.25),
      soundManager.loadSound('zoom-in', `${ASSET_BASE}/assets/sounds/zoom-in.mp3`, false, 0.5),
      soundManager.loadSound('missile-fire', `${ASSET_BASE}/assets/sounds/missile-firing-1.mp3`, false, 0.75),
      soundManager.loadSound('m61-firing', `${ASSET_BASE}/assets/sounds/m61-firing.mp3`, true, 0.75),
      soundManager.loadSound('rwr-tws', `${ASSET_BASE}/assets/sounds/rwr-tws.mp3`, true, 0.2),
      soundManager.loadSound('rwr-lock', `${ASSET_BASE}/assets/sounds/rwr-lock.mp3`, false, 0.2),
      soundManager.loadSound('wind', `${ASSET_BASE}/assets/sounds/wind.mp3`, true, 0.25),
      soundManager.loadSound('terrain-pull-up', `${ASSET_BASE}/assets/sounds/terrain-pull-up.mp3`, false, 0.9),
      soundManager.loadSound('warning', `${ASSET_BASE}/assets/sounds/warning.mp3`, false, 0.6),
      soundManager.loadSound('glitch-1', `${ASSET_BASE}/assets/sounds/glitch-transition-1.mp3`, false, 0.25),
      soundManager.loadSound('glitch-2', `${ASSET_BASE}/assets/sounds/glitch-transition-2.mp3`, false, 0.25),
      soundManager.loadSound('glitch-3', `${ASSET_BASE}/assets/sounds/glitch-transition-3.mp3`, false, 0.25),
      soundManager.loadSound('glitch-4', `${ASSET_BASE}/assets/sounds/glitch-transition-4.mp3`, false, 0.25),
    ]);

    loadingStatus.audio = true;
    updateLoadingUI();
    setupButtonSounds();
  }

  function initThree() {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(75, 1, 0.1, 100000);
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.pointerEvents = 'none';
    threeContainer.appendChild(renderer.domElement);
    threeContainer.classList.add('hidden');

    scene.add(new THREE.AmbientLight(0xffffff, 1.0));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(5, 10, 5);
    scene.add(directionalLight);

    scene.children.forEach((child) => child.layers.enable(1));

    try {
      particles.init(scene, getViewer());
    } catch {
      // ignore particle init issues and continue booting the simulator
    }

    initSounds().catch((error) => {
      console.warn('FlightSim audio initialization failed, continuing without full audio preload', error);
      loadingStatus.audio = true;
      updateLoadingUI();
    });

    let aircraftReady = false;
    let aircraftReadyTimeout: number | null = null;

    const finalizeAircraftSetup = (mesh: THREE.Object3D, animations: THREE.AnimationClip[] = []) => {
      if (aircraftReady) return;
      aircraftReady = true;
      if (aircraftReadyTimeout !== null) {
        window.clearTimeout(aircraftReadyTimeout);
        aircraftReadyTimeout = null;
      }

      mesh.rotation.y += Math.PI / 2;
      mesh.updateMatrixWorld(true);

      planeModel = new THREE.Group();
      planeModel.add(mesh);
      scene.add(planeModel);

      const box = new THREE.Box3().setFromObject(mesh);
      const center = box.getCenter(new THREE.Vector3());
      mesh.position.sub(center);
      mesh.updateMatrixWorld(true);

      planeModel.position.copy(BASE_PLANE_POS);
      planeModel.scale.set(0.2, 0.2, 0.2);

      const flameL = new JetFlame();
      const flameR = new JetFlame();
      flameL.group.position.set(-0.4, -0.065, 5);
      flameR.group.position.set(0.4, -0.065, 5);
      planeModel.add(flameL.group);
      planeModel.add(flameR.group);
      jetFlames = [flameL, flameR];

      planeModel.layers.set(1);
      planeModel.traverse((child) => child.layers.set(1));

      weaponSystem = new WeaponSystem(getViewer(), scene, planeModel);
      weaponSystem.onKill = (npc: any) => {
        state.score += 1000;
        soundManager.play('glitch-random');
        hud.showKillNotification(npc.name, 1000);
      };
      state.weaponSystem = weaponSystem;

      mixer = new THREE.AnimationMixer(mesh);
      const clip = THREE.AnimationClip.findByName(animations, 'flight_mode');
      if (clip) {
        const action = mixer.clipAction(clip);
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
        action.play();
      }

      loadingStatus.model = true;
      updateLoadingUI();
    };

    const createFallbackAircraft = () => {
      const material = new THREE.MeshStandardMaterial({ color: 0xaeb8c2, metalness: 0.35, roughness: 0.45 });
      const fallbackJet = new THREE.Group();

      const fuselage = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.8, 10, 12), material);
      fuselage.rotation.z = Math.PI / 2;
      fallbackJet.add(fuselage);

      const nose = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1.8, 12), material);
      nose.rotation.z = -Math.PI / 2;
      nose.position.x = 5.6;
      fallbackJet.add(nose);

      const wing = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.12, 1.4), material);
      wing.position.set(0.2, 0, 0);
      fallbackJet.add(wing);

      const tailWing = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.1, 0.7), material);
      tailWing.position.set(-3.3, 0.18, 0);
      fallbackJet.add(tailWing);

      const verticalTail = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.9, 0.12), material);
      verticalTail.position.set(-3.8, 0.75, 0);
      fallbackJet.add(verticalTail);

      return fallbackJet;
    };

    aircraftReadyTimeout = window.setTimeout(() => {
      console.warn('FlightSim aircraft model load timed out, using fallback placeholder jet instead');
      finalizeAircraftSetup(createFallbackAircraft());
    }, 4000);
    addCleanup.push(() => {
      if (aircraftReadyTimeout !== null) {
        window.clearTimeout(aircraftReadyTimeout);
      }
    });

    const loader = new GLTFLoader();
    loader.load(
      `${ASSET_BASE}/assets/models/f-15.glb`,
      (gltf) => {
        finalizeAircraftSetup(gltf.scene, gltf.animations);
      },
      undefined,
      (error) => {
        console.warn('FlightSim aircraft model missing, using fallback placeholder jet instead', error);
        finalizeAircraftSetup(createFallbackAircraft());
      }
    );
  }

  function applySize(force = false) {
    if (!renderer || !camera) return;

    const rect = root.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    if (!force && width === lastWidth && height === lastHeight) return;

    lastWidth = width;
    lastHeight = height;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);

    const viewer = getViewer();
    if (viewer) viewer.resize();
    hud.resizeMinimap();
  }

  function update(dt: number) {
    if (currentState !== States.FLYING) return;

    const input = controller.update(dt);
    const physicsResult = physics.update(input, dt);

    const prevSpeed = state.speed;
    state.speed = physicsResult.speed;
    state.pitch = physicsResult.pitch;
    state.roll = physicsResult.roll;
    state.heading = physicsResult.heading;
    state.throttle = input.throttle;
    state.yaw = input.yaw;
    state.isBoosting = physicsResult.isBoosting;
    state.weaponSystem = weaponSystem;
    state.npcs = npcSystem ? npcSystem.npcs : [];

    const viewer = getViewer();
    if (viewer?.camera?.frustum && 'fovy' in viewer.camera.frustum) {
      zoomBlend = THREE.MathUtils.lerp(zoomBlend, input.zoom ? 1 : 0, 0.18);
      (viewer.camera.frustum as any).fov = Cesium.Math.toRadians(
        THREE.MathUtils.lerp(DEFAULT_FLIGHT_FOV, SECONDARY_ZOOM_FOV, zoomBlend)
      );
    }

    if (weaponSystem) {
      weaponSystem.secondaryActive = !!input.fireSecondary || !!input.zoom;
      if (input.weaponIndex !== -1) weaponSystem.selectWeapon(input.weaponIndex);
      if (input.toggleWeapon) weaponSystem.toggleWeapon();
      if (input.fire) weaponSystem.fire(state);
      if (input.fireSecondary) weaponSystem.fire(state, 'missile');
      if (input.fireFlare) weaponSystem.fireFlare(state);
      weaponSystem.update(dt, state, input);
    }

    const newPos = movePosition(state.lon, state.lat, state.alt, state.heading, state.pitch, state.speed * dt);
    state.lon = newPos.lon;
    state.lat = newPos.lat;
    state.alt = newPos.alt;

    const nowTime = Date.now();
    const distFromLast = calculateDistance(state.lon, state.lat, lastGeocodePos.lon, lastGeocodePos.lat);
    if (nowTime - lastGeocodeTime > GEOCODE_INTERVAL || distFromLast > GEOCODE_MIN_DIST) {
      lastGeocodeTime = nowTime;
      lastGeocodePos = { lon: state.lon, lat: state.lat };
      reverseGeocode(state.lon, state.lat).then((name) => {
        if (!destroyed && name && name !== currentRegionName) {
          currentRegionName = name;
          hud.showRegion(name);
        }
      });
    }

    checkCrash();
    checkGPWS();

    if (soundManager.isPlaying('jet-engine')) {
      const speedFactor = Math.max(0, Math.min(1, (state.speed - 100) / 900));
      soundManager.setVolume('jet-engine', 0.5 + speedFactor * 0.1);
    }

    if (state.isBoosting && !lastIsBoosting) {
      soundManager.play('boost');
    }

    if (state.throttle > lastThrottleLevel + 0.01 && !soundManager.isPlaying('throttle')) {
      soundManager.play('throttle');
    }
    lastThrottleLevel = state.throttle;

    if (Math.abs(input.pitch) > 0.5) {
      if (!soundManager.isPlaying('pitch')) soundManager.play('pitch', 0.1);
    } else if (soundManager.isPlaying('pitch')) {
      soundManager.stop('pitch', 0.1);
    }

    if (Math.abs(input.roll) > 0.5 || Math.abs(input.yaw) > 0.5) {
      if (!soundManager.isPlaying('roll')) soundManager.play('roll', 0.1);
    } else if (soundManager.isPlaying('roll')) {
      soundManager.stop('roll', 0.1);
    }

    const planeHPR = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(state.heading),
      Cesium.Math.toRadians(state.pitch),
      Cesium.Math.toRadians(state.roll)
    );

    setCameraToPlane(
      state.lon,
      state.lat,
      state.alt,
      Cesium.Math.toDegrees(planeHPR.heading),
      Cesium.Math.toDegrees(planeHPR.pitch),
      Cesium.Math.toDegrees(planeHPR.roll)
    );

    npcSystem?.update(dt, state);
    hud.update(state, npcSystem ? npcSystem.npcs : []);

    if (planeModel) {
      const accel = (state.speed - prevSpeed) / dt;
      const accelInertia = Math.max(-0.5, Math.min(1.5, accel * 0.001));
      let targetZ = BASE_PLANE_POS.z - accelInertia;

      if (physicsResult.isBoosting && !lastIsBoosting) {
        boostRollDirection = Math.random() > 0.5 ? 1 : -1;
      }

      let boostZOffset = 0;
      if (physicsResult.isBoosting) {
        const T = physicsResult.boostDuration;
        const p = Math.max(0, Math.min(1, 1 - physicsResult.boostTimeRemaining / T));

        if (p < 0.2) {
          const localP = p / 0.2;
          boostZOffset = -(localP * localP) * 1.5;
          boostRoll = 0;
        } else if (p < 0.8) {
          const localP = (p - 0.2) / 0.6;
          boostZOffset = -1.5;
          const easedP = localP < 0.5 ? 4 * localP * localP * localP : 1 - Math.pow(-2 * localP + 2, 3) / 2;
          boostRoll = easedP * (Math.PI * 2 * physicsResult.boostRotations) * boostRollDirection;
        } else {
          const localP = (p - 0.8) / 0.2;
          const easedReturn = localP * localP * (3 - 2 * localP);
          boostZOffset = -1.5 + easedReturn * 0.7;
          boostRoll = Math.PI * 2 * physicsResult.boostRotations * boostRollDirection;
        }
      } else {
        boostRoll = 0;
        boostZOffset = 0;
      }
      lastIsBoosting = physicsResult.isBoosting;

      const zLerp = physicsResult.isBoosting ? 10 * dt : 2 * dt;
      currentBoostZOffset += (boostZOffset - currentBoostZOffset) * zLerp;
      targetZ += currentBoostZOffset;

      const time = performance.now() * 0.001;
      const idleX = Math.sin(time * 0.8) * 0.035;
      const idleY = Math.cos(time * 0.6) * 0.025;
      const idleRotX = Math.sin(time * 0.5) * 0.015;
      const idleRotY = Math.cos(time * 0.4) * 0.015;
      const idleRotZ = Math.sin(time * 0.7) * 0.025;

      const targetX = BASE_PLANE_POS.x - input.roll * 0.6 - input.yaw * 0.12 + idleX;
      const targetY = BASE_PLANE_POS.y - input.pitch * 0.1 + idleY;
      const targetRotZ = THREE.MathUtils.degToRad(-input.roll * 15) + idleRotZ;
      const targetRotX = THREE.MathUtils.degToRad(input.pitch * 10) + idleRotX;
      const targetRotY = THREE.MathUtils.degToRad(-input.yaw * 4) + idleRotY;

      const lerpFactor = physicsResult.isBoosting ? 3 * dt : 5 * dt;
      visualOffset.x += (targetX - visualOffset.x) * lerpFactor;
      visualOffset.y += (targetY - visualOffset.y) * lerpFactor;
      visualOffset.z += (targetZ - visualOffset.z) * lerpFactor;
      visualRotation.z += (targetRotZ - visualRotation.z) * lerpFactor;
      visualRotation.x += (targetRotX - visualRotation.x) * lerpFactor;
      visualRotation.y += (targetRotY - visualRotation.y) * lerpFactor;

      planeModel.position.copy(visualOffset);

      const flightLagQ = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(visualRotation.x, visualRotation.y, visualRotation.z + boostRoll)
      );

      planeModel.quaternion.copy(flightLagQ);

      jetFlames.forEach((flame) => flame.update(state.throttle, Boolean(state.isBoosting), time, dt));
    }
  }

  function checkGPWS() {
    if (currentState !== States.FLYING) {
      hud.setPullUpWarning(false);
      return;
    }

    const viewer = getViewer();
    if (!viewer) return;

    const cartographic = Cesium.Cartographic.fromDegrees(state.lon, state.lat);
    const terrainHeight = viewer.scene.globe.getHeight(cartographic);
    if (terrainHeight === undefined) return;

    const agl = state.alt - terrainHeight;
    const verticalSpeed = state.speed * Math.sin(Cesium.Math.toRadians(state.pitch));
    let showWarning = false;

    if (state.pitch < -1 && agl < 450) {
      if (agl < 150 || verticalSpeed < -20) {
        showWarning = true;
      }
    }

    hud.setPullUpWarning(showWarning);

    if (showWarning) {
      const now = Date.now();
      if (!gpwsActive || (now - lastGPWSWarningTime > GPWS_COOLDOWN && !soundManager.isPlaying('terrain-pull-up'))) {
        soundManager.play('terrain-pull-up');
        lastGPWSWarningTime = now;
      }
      gpwsActive = true;
    } else if (gpwsActive) {
      soundManager.stop('terrain-pull-up', 0.1);
      gpwsActive = false;
    }
  }

  function checkCrash() {
    if (currentState !== States.FLYING) return;

    const now = Date.now();
    if (now - lastCrashCheck < 100 || now - flightStartTime < 3000) return;
    lastCrashCheck = now;

    const viewer = getViewer();
    if (!viewer) return;

    const cartographic = Cesium.Cartographic.fromDegrees(state.lon, state.lat);
    const terrainHeight = viewer.scene.globe.getHeight(cartographic);
    if (terrainHeight !== undefined && state.alt <= terrainHeight + 5) {
      currentState = States.CRASHED;
      dialogueSystem.stop();
      uiContainer?.classList.add('hidden');
      byId('weapons-hud')?.classList.add('hidden');
      threeContainer.classList.add('hidden');
      crashMenu?.classList.remove('hidden');
      hud.update(state, []);
      stopAllFlyingSounds(0.1);
      window.setTimeout(() => {
        if (destroyed) return;
        soundManager.play('explode');
        soundManager.play('ambient-crash');
      }, 50);
    }
  }

  function returnToMainMenu() {
    closeAllModals();
    dialogueSystem.stop();
    stopAllFlyingSounds(0.1);
    currentState = States.MENU;
    syncPointerLockState();
    mainMenu?.classList.remove('hidden');
    pauseMenu?.classList.add('hidden');
    crashMenu?.classList.add('hidden');
    spawnInstruction?.classList.add('hidden');
    uiContainer?.classList.add('hidden');
    byId('weapons-hud')?.classList.add('hidden');
    threeContainer.classList.add('hidden');
    setRenderOptimization(true);
    setControlsEnabled(false);
    updateLoadingUI();

    if (spawnMarker) {
      getViewer()?.entities.remove(spawnMarker);
      spawnMarker = null;
    }

    if (initialCameraView) {
      getViewer()?.camera.flyTo({ ...initialCameraView, duration: 2.5 });
    }
  }

  function enterSpawnPicking(useVignette = true) {
    state.score = 0;
    npcSystem?.clear();
    stopAllFlyingSounds(0.3);
    soundManager.play('zoom-in');
    soundManager.play('wind', 1.0);
    const vignette = byId<HTMLDivElement>('transition-vignette');
    if (useVignette && vignette) vignette.style.opacity = '1';

    window.setTimeout(() => {
      spawnInstruction?.classList.remove('hidden');
      threeContainer.classList.add('hidden');
      uiContainer?.classList.add('hidden');
      byId('weapons-hud')?.classList.add('hidden');
      currentState = States.PICK_SPAWN;
      syncPointerLockState();
      confirmSpawnBtn?.classList.add('hidden');

      const searchInput = byId<HTMLInputElement>('locationSearch');
      const instructionText = byId<HTMLSpanElement>('instruction-text');
      const resultsContainer = byId<HTMLDivElement>('search-results');

      if (searchInput) {
        searchInput.value = '';
        searchInput.style.display = 'none';
      }
      if (instructionText) {
        instructionText.style.display = 'block';
        instructionText.textContent = 'CLICK ANYWHERE ON THE MAP TO CHOOSE SPAWN POINT';
      }
      if (resultsContainer) {
        resultsContainer.style.display = 'none';
      }

      setControlsEnabled(true);
      if (spawnMarker) {
        getViewer()?.entities.remove(spawnMarker);
        spawnMarker = null;
      }

      getViewer()?.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(state.lon, state.lat, 15000),
        duration: 2,
        complete: () => {
          if (vignette) vignette.style.opacity = '0';
        },
      });
    }, useVignette ? 500 : 0);
  }

  function exitSpawnPicking() {
    soundManager.play('zoom-in');
    soundManager.stop('wind', 1.0);
    stopAllFlyingSounds(0.3);
    spawnInstruction?.classList.add('hidden');
    confirmSpawnBtn?.classList.add('hidden');
    mainMenu?.classList.remove('hidden');
    currentState = States.MENU;
    syncPointerLockState();
    loadingIndicator?.classList.add('hidden');
    setRenderOptimization(true);
    setControlsEnabled(false);

    if (spawnMarker) {
      getViewer()?.entities.remove(spawnMarker);
      spawnMarker = null;
    }

    if (initialCameraView) {
      getViewer()?.camera.flyTo({ ...initialCameraView, duration: 2.5 });
    }
  }

  function setupSpawnPicker() {
    const viewer = getViewer();
    if (!viewer) return;

    const instructionText = byId<HTMLSpanElement>('instruction-text');
    spawnHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    spawnHandler.setInputAction((click: any) => {
      if (currentState !== States.PICK_SPAWN) return;

      const ray = viewer.camera.getPickRay(click.position);
      const cartesian = ray ? viewer.scene.globe.pick(ray, viewer.scene) : null;
      if (!cartesian) return;

      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      const lon = Cesium.Math.toDegrees(cartographic.longitude);
      const lat = Cesium.Math.toDegrees(cartographic.latitude);
      state.lon = lon;
      state.lat = lat;
      state.alt = Math.max(0, cartographic.height) + 1500;

      if (instructionText) instructionText.textContent = 'FETCHING LOCATION INFO...';
      reverseGeocode(lon, lat).then((regionName) => {
        if (!destroyed && regionName && currentState === States.PICK_SPAWN) {
          if (instructionText) instructionText.textContent = regionName;
          if (spawnMarker?.label) {
            spawnMarker.label.text = regionName;
          }
        }
      }).catch(() => undefined);

      Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, [cartographic])
        .then(([point]) => {
          state.alt = Math.max(0, point.height || 0) + 1500;
        })
        .catch(() => undefined);

      if (spawnMarker) viewer.entities.remove(spawnMarker);
      spawnMarker = viewer.entities.add({
        position: cartesian,
        point: {
          pixelSize: 15,
          color: Cesium.Color.RED,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        label: {
          text: 'Target Spawn Location',
          font: `14pt ${getComputedStyle(root).fontFamily}`,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          outlineWidth: 2,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -20),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });

      confirmSpawnBtn?.classList.remove('hidden');
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  function setupLocationSearch() {
    const searchInput = byId<HTMLInputElement>('locationSearch');
    const resultsContainer = byId<HTMLDivElement>('search-results');
    const instructionText = byId<HTMLSpanElement>('instruction-text');
    const searchToggleBtn = byId<HTMLDivElement>('search-toggle-btn');
    if (!searchInput || !resultsContainer || !instructionText || !searchToggleBtn) return;

    const originalSearchIcon = searchToggleBtn.innerHTML;
    let debounceTimer: number | undefined;

    listenElement(searchToggleBtn, 'click', (event) => {
      event.stopPropagation();
      const isSearching = searchInput.style.display === 'block';
      if (isSearching) {
        searchInput.style.display = 'none';
        instructionText.style.display = 'block';
        resultsContainer.style.display = 'none';
      } else {
        searchInput.style.display = 'block';
        instructionText.style.display = 'none';
        searchInput.focus();
      }
    });

    listenElement(searchInput, 'input', (event) => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      const queryText = (event.target as HTMLInputElement).value.trim();
      if (queryText.length < 3) {
        resultsContainer.style.display = 'none';
        return;
      }

      debounceTimer = window.setTimeout(async () => {
        searchToggleBtn.innerHTML = '<div class="loader-spinner"></div>';
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryText)}&limit=5`);
          const data = await response.json();
          resultsContainer.innerHTML = '';

          if (data.length > 0) {
            data.forEach((item: any) => {
              const div = document.createElement('div');
              div.textContent = item.display_name;
              div.style.padding = '10px';
              div.style.cursor = 'pointer';
              div.onclick = () => {
                const lon = parseFloat(item.lon);
                const lat = parseFloat(item.lat);
                const viewer = getViewer();
                if (!viewer) return;

                state.lon = lon;
                state.lat = lat;
                state.alt = 1500;

                const cartographic = Cesium.Cartographic.fromDegrees(lon, lat);
                Cesium.sampleTerrainMostDetailed(viewer.terrainProvider, [cartographic])
                  .then(([point]) => {
                    state.alt = Math.max(0, point.height || 0) + 1500;
                  })
                  .catch(() => undefined);

                viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
                viewer.camera.flyTo({
                  destination: Cesium.Cartesian3.fromDegrees(lon, lat, 15000),
                  duration: 1.5,
                });

                if (spawnMarker) viewer.entities.remove(spawnMarker);
                spawnMarker = viewer.entities.add({
                  position: Cesium.Cartesian3.fromDegrees(lon, lat),
                  point: {
                    pixelSize: 15,
                    color: Cesium.Color.RED,
                    outlineColor: Cesium.Color.WHITE,
                    outlineWidth: 2,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                  },
                  label: {
                    text: item.display_name.split(',')[0],
                    font: `14pt ${getComputedStyle(root).fontFamily}`,
                    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                    outlineWidth: 2,
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    pixelOffset: new Cesium.Cartesian2(0, -20),
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                  },
                });

                confirmSpawnBtn?.classList.remove('hidden');
                resultsContainer.style.display = 'none';
                searchInput.style.display = 'none';
                instructionText.style.display = 'block';
                instructionText.textContent = item.display_name.split(',')[0].toUpperCase();
                searchInput.value = item.display_name;
              };
              resultsContainer.appendChild(div);
            });
            resultsContainer.style.display = 'block';
          } else {
            resultsContainer.style.display = 'none';
          }
        } catch (error) {
          console.error('FlightSim search error', error);
        } finally {
          searchToggleBtn.innerHTML = originalSearchIcon;
        }
      }, 500);
    });

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!searchInput.contains(target) && !resultsContainer.contains(target) && !searchToggleBtn.contains(target)) {
        resultsContainer.style.display = 'none';
        if (searchInput.style.display === 'block') {
          searchInput.style.display = 'none';
          instructionText.style.display = 'block';
        }
      }
    };

    listenDocument(document, 'click', handleOutsideClick);
  }

  function setupModalListeners() {
    const openModal = (buttonId: string, modalId: string, beforeOpen?: () => void) => {
      const button = byId<HTMLElement>(buttonId);
      const modal = byId<HTMLElement>(modalId);
      if (!button || !modal) return;
      listenElement(button, 'click', () => {
        closeAllModals();
        beforeOpen?.();
        modal.classList.remove('hidden');
      });
    };

    openModal('helpBtn', 'helpModal');
    openModal('optionsBtn', 'optionsModal', updateSettingsUI);
    openModal('pauseOptionsBtn', 'optionsModal', updateSettingsUI);
    openModal('pauseHelpBtn', 'helpModal');
    openModal('creditsBtn', 'creditsModal');
    openModal('aboutBtn', 'aboutBtnModal');

    const sensitivitySlider = byId<HTMLInputElement>('sensitivitySlider');
    const sensitivityValue = byId<HTMLSpanElement>('sensitivityValue');
    if (sensitivitySlider && sensitivityValue) {
      listenElement(sensitivitySlider, 'input', (event) => {
        sensitivityValue.textContent = (event.target as HTMLInputElement).value;
      });
    }

    const saveOptionsBtn = byId<HTMLButtonElement>('saveOptionsBtn');
    if (saveOptionsBtn) {
      listenElement(saveOptionsBtn, 'click', () => {
        gameSettings.graphicsQuality = byId<HTMLSelectElement>('graphicsQuality')?.value ?? gameSettings.graphicsQuality;
        gameSettings.antialiasing = byId<HTMLInputElement>('antialiasing')?.checked ?? gameSettings.antialiasing;
        gameSettings.fogEffects = byId<HTMLInputElement>('fogEffects')?.checked ?? gameSettings.fogEffects;
        gameSettings.mouseSensitivity = parseFloat(byId<HTMLInputElement>('sensitivitySlider')?.value ?? `${gameSettings.mouseSensitivity}`);
        gameSettings.showHud = byId<HTMLInputElement>('showHud')?.checked ?? gameSettings.showHud;
        gameSettings.showHorizonLines = byId<HTMLInputElement>('showHorizonLines')?.checked ?? gameSettings.showHorizonLines;
        gameSettings.soundEnabled = byId<HTMLInputElement>('soundEnabled')?.checked ?? gameSettings.soundEnabled;
        gameSettings.minimapRange = parseInt(byId<HTMLSelectElement>('minimapRange')?.value ?? `${gameSettings.minimapRange}`, 10);
        saveSettings();
        applySettings();
        closeAllModals();
      });
    }

    root.querySelectorAll<HTMLElement>('.close-modal').forEach((button) => {
      listenElement(button, 'click', (event) => {
        event.stopPropagation();
        button.closest('.modal')?.classList.add('hidden');
      });
    });

    const handleModalBackdropClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.classList.contains('modal') && root.contains(target)) {
        target.classList.add('hidden');
      }
    };

    listenDocument(document, 'click', handleModalBackdropClick);
  }

  function setupUiActions() {
    setupModalListeners();

    const startBtn = byId<HTMLButtonElement>('startBtn');
    if (startBtn) {
      listenElement(startBtn, 'click', () => {
        closeAllModals();
        mainMenu?.classList.add('hidden');
        enterSpawnPicking(false);
      });
    }

    const resumeBtn = byId<HTMLButtonElement>('resumeBtn');
    if (resumeBtn) {
      listenElement(resumeBtn, 'click', () => {
        closeAllModals();
        pauseMenu?.classList.add('hidden');
        uiContainer?.classList.remove('hidden');
        byId('weapons-hud')?.classList.remove('hidden');
        currentState = States.FLYING;
        syncPointerLockState();
        controller.requestPointerLock();
        dialogueSystem.resume();
        resumeGameplaySounds();
      });
    }

    const restartBtn = byId<HTMLButtonElement>('restartBtn');
    if (restartBtn) {
      listenElement(restartBtn, 'click', () => {
        closeAllModals();
        pauseMenu?.classList.add('hidden');
        dialogueSystem.stop();
        enterSpawnPicking(true);
      });
    }

    const quitBtn = byId<HTMLButtonElement>('quitBtn');
    if (quitBtn) {
      listenElement(quitBtn, 'click', () => {
        returnToMainMenu();
      });
    }

    const respawnBtn = byId<HTMLButtonElement>('respawnBtn');
    if (respawnBtn) {
      listenElement(respawnBtn, 'click', () => {
        closeAllModals();
        crashMenu?.classList.add('hidden');
        dialogueSystem.stop();
        enterSpawnPicking(true);
      });
    }

    if (confirmSpawnBtn) {
      listenElement(confirmSpawnBtn, 'click', () => {
        const vignette = byId<HTMLDivElement>('transition-vignette');
        if (vignette) vignette.style.opacity = '1';
        soundManager.play('spawn');

        window.setTimeout(() => {
          const viewer = getViewer();
          if (!viewer) return;

          if (spawnMarker) {
            viewer.entities.remove(spawnMarker);
            spawnMarker = null;
          }

          setControlsEnabled(false);
          state.speed = 100;
          state.pitch = 0;
          state.roll = 0;
          try {
            state.heading = typeof viewer.camera.heading === 'number' ? Cesium.Math.toDegrees(viewer.camera.heading) : 0;
          } catch {
            state.heading = 0;
          }

          currentRegionName = null;
          lastGeocodeTime = 0;
          lastGeocodePos = { lon: 0, lat: 0 };
          visualOffset.copy(BASE_PLANE_POS);
          visualRotation.set(0, 0, 0);
          boostRoll = 0;
          currentBoostZOffset = 0;
          boostRollDirection = 1;
          lastIsBoosting = false;

          controller.reset();
          physics = new PlanePhysics();
          physics.reset(state.lon, state.lat, state.alt, state.heading, state.pitch, state.roll);
          hud.resetTime();
          hud.resizeMinimap();
          weaponSystem?.resetAmmo?.();
          npcSystem?.spawnNPC(state.lon, state.lat, state.alt);

          spawnInstruction?.classList.add('hidden');
          confirmSpawnBtn.classList.add('hidden');
          loadingIndicator?.classList.add('hidden');
          currentState = States.TRANSITIONING;
          syncPointerLockState();
          setRenderOptimization(false);

          viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(state.lon, state.lat, state.alt),
            orientation: {
              heading: Cesium.Math.toRadians(state.heading),
              pitch: Cesium.Math.toRadians(state.pitch),
              roll: Cesium.Math.toRadians(state.roll),
            },
            duration: 2,
            easingFunction: Cesium.EasingFunction.QUADRATIC_IN_OUT,
            complete: () => {
              flightStartTime = Date.now();
              uiContainer?.classList.remove('hidden');
              byId('weapons-hud')?.classList.remove('hidden');
              threeContainer.classList.remove('hidden');
              hud.resizeMinimap();
              currentState = States.FLYING;
              syncPointerLockState();
              soundManager.play('jet-engine', 1.0);
              if (vignette) vignette.style.opacity = '0';
              dialogueSystem.start();
            },
          });
        }, 500);
      });
    }
  }

  function setupGlobalEvents() {
    listen(window, 'keydown', (event) => {
      const key = event.key.toLowerCase();
      if (key === 'escape') {
        const openModals = Array.from(root.querySelectorAll('.modal')).filter((modal) => !modal.classList.contains('hidden'));
        if (openModals.length > 0) {
          openModals.forEach((modal) => modal.classList.add('hidden'));
          return;
        }
      }

      if (key === 'escape' || key === 'p') {
        if (currentState === States.FLYING) {
          currentState = States.PAUSED;
          syncPointerLockState();
          dialogueSystem.pause();
          uiContainer?.classList.add('hidden');
          byId('weapons-hud')?.classList.add('hidden');
          pauseMenu?.classList.remove('hidden');
          hud.resizeMinimap();
          pauseGameplaySounds();
          hud.update(state, []);
        } else if (currentState === States.PAUSED) {
          currentState = States.FLYING;
          syncPointerLockState();
          dialogueSystem.resume();
          pauseMenu?.classList.add('hidden');
          uiContainer?.classList.remove('hidden');
          byId('weapons-hud')?.classList.remove('hidden');
          resumeGameplaySounds();
        } else if (currentState === States.PICK_SPAWN && key === 'escape') {
          exitSpawnPicking();
        }
      }

      if (key === 'z' && currentState === States.FLYING) {
        dialogueSystem.skip();
      }
    });

    listenElement(root, 'click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target || !root.contains(target)) return;
      if (currentState !== States.FLYING) return;
      if (target.closest('.modal, button, a, input, select, textarea, label')) return;
      controller.requestPointerLock();
    });

    listenDocument(document, 'visibilitychange', () => {
      if (document.hidden && currentState === States.FLYING) {
        currentState = States.PAUSED;
        syncPointerLockState();
        dialogueSystem.pause();
        uiContainer?.classList.add('hidden');
        pauseMenu?.classList.remove('hidden');
        hud.resizeMinimap();
        pauseGameplaySounds();
        hud.update(state, []);
      }
    });

    listen(window, 'blur', () => {
      if (currentState === States.FLYING) {
        currentState = States.PAUSED;
        syncPointerLockState();
        dialogueSystem.pause();
        uiContainer?.classList.add('hidden');
        pauseMenu?.classList.remove('hidden');
        hud.resizeMinimap();
        pauseGameplaySounds();
        hud.update(state, []);
      }
    });

    listenElement(root, 'contextmenu', (event) => {
      event.preventDefault();
    });
  }

  initCesium({
    mainContainer: cesiumContainer,
    minimapContainer,
    pauseMinimapContainer,
  });

  loadingStatus.cesium = true;
  updateLoadingUI();

  const viewer = getViewer();
  if (!viewer) {
    loadingStatus.failed = true;
    updateLoadingUI();
    return;
  }

  let globeLoadingStarted = false;
  globeTrackerCleanup = viewer.scene.postRender.addEventListener(() => {
    const tilesLoaded = viewer.scene.globe.tilesLoaded;
    if (!tilesLoaded) globeLoadingStarted = true;
    if (tilesLoaded) {
      const surface = (viewer.scene.globe as any)._surface;
      const hasTiles = surface && surface._tilesToRender && surface._tilesToRender.length > 0;
      if (hasTiles || globeLoadingStarted) {
        loadingStatus.globe = true;
        updateLoadingUI();
        globeTrackerCleanup?.();
        globeTrackerCleanup = null;
      }
    }
  });

  const globeReadyTimeout = window.setTimeout(() => {
    if (!loadingStatus.globe) {
      loadingStatus.globe = true;
      updateLoadingUI();
      globeTrackerCleanup?.();
      globeTrackerCleanup = null;
    }
  }, 3000);
  addCleanup.push(() => window.clearTimeout(globeReadyTimeout));

  viewer.scene.globe.tileLoadProgressEvent.addEventListener((queueLength: number) => {
    if (!loadingIndicator || !loadingText) return;
    if (currentState === States.PICK_SPAWN) {
      if (queueLength > 0) {
        loadingText.textContent = 'Loading Terrain...';
        loadingIndicator.classList.remove('hidden');
      } else {
        loadingIndicator.classList.add('hidden');
      }
      return;
    }

    const isAllLoaded = loadingStatus.audio && loadingStatus.model && loadingStatus.cesium && loadingStatus.globe;
    if (isAllLoaded) {
      loadingIndicator.classList.add('hidden');
    }
  });

  const resumeAudio = () => {
    const listener = soundManager.listener;
    if (listener?.context?.state === 'suspended') {
      listener.context.resume().catch(() => undefined);
    }
    window.removeEventListener('mousedown', resumeAudio);
    window.removeEventListener('keydown', resumeAudio);
  };
  listen(window, 'mousedown', resumeAudio);
  listen(window, 'keydown', resumeAudio);

  initialCameraView = {
    destination: viewer.camera.position.clone(),
    orientation: {
      heading: viewer.camera.heading,
      pitch: viewer.camera.pitch,
      roll: viewer.camera.roll,
    },
  };

  initThree();
  npcSystem = new NPCSystem(viewer, scene, new GLTFLoader());
  setupSpawnPicker();
  setupLocationSearch();
  setupUiActions();
  setupGlobalEvents();
  loadSettings();
  uiContainer?.classList.add('hidden');
  threeContainer.classList.add('hidden');
  setMenuTimeNow();
  updateLoadingUI();
  applySize(true);
  initUserLocation().catch(() => undefined);
  syncPointerLockState();

  const resizeObserver = new ResizeObserver(() => applySize(true));
  resizeObserver.observe(root);
  addCleanup.push(() => resizeObserver.disconnect());

  const system = {
    update(dt: number) {
      if (destroyed) return;

      applySize();
      elapsedForMenuClock += dt;
      if (elapsedForMenuClock >= 1) {
        elapsedForMenuClock = 0;
        setMenuTimeNow();
      }

      frameCount += 1;
      const now = performance.now();
      if (now - lastFpsUpdate >= 1000) {
        fps = (frameCount * 1000) / (now - lastFpsUpdate);
        frameCount = 0;
        lastFpsUpdate = now;
        hud.updateFPS(fps);
      }

      if (currentState === States.FLYING || currentState === States.PAUSED || currentState === States.TRANSITIONING) {
        renderer.autoClear = false;
        renderer.clear();

        const viewer = getViewer();
        if (viewer?.camera?.frustum && 'fovy' in viewer.camera.frustum) {
          const targetFov = Cesium.Math.toDegrees((viewer.camera.frustum as Cesium.PerspectiveFrustum).fovy);
          camera.fov = targetFov;
          camera.aspect = Math.max(1, lastWidth) / Math.max(1, lastHeight);
          camera.updateProjectionMatrix();
        }

        camera.layers.set(0);
        if (currentState === States.FLYING) {
          update(dt);
        } else if (currentState === States.PAUSED) {
          hud.updatePauseMenu(state, currentRegionName, npcSystem ? npcSystem.npcs : []);
        }

        mixer?.update(dt);
        if (currentState === States.FLYING) {
          try {
            particles.update(dt);
          } catch {
            // ignore transient particle update failures
          }
        }

        renderer.render(scene, camera);

        renderer.clearDepth();
        camera.fov = 75;
        camera.updateProjectionMatrix();
        camera.layers.set(1);
        renderer.render(scene, camera);
      } else {
        threeContainer.classList.add('hidden');
      }
    },
  };

  engine.addSystem(system);

  return () => {
    destroyed = true;
    engine.removeSystem(system);
    globeTrackerCleanup?.();
    globeTrackerCleanup = null;
    spawnHandler?.destroy();
    spawnHandler = null;
    addCleanup.forEach((cleanup) => cleanup());
    controller.destroy();
    dialogueSystem.stop();
    stopAllFlyingSounds(0);
    soundManager.stopAll(0);
    soundManager.sounds?.clear?.();
    npcSystem?.clear();
    particles.list?.splice?.(0, particles.list.length);

    if (renderer) {
      renderer.dispose();
      renderer.domElement.remove();
    }

    destroyCesium();
  };
}